import { useState } from 'react'

import { Notice } from '@/components/ui'
import { useSendOrder } from '@/hooks/useSendOrder'
import type { PlanRequest, PlanResponse } from '@/lib/types'

/**
 * Envoi de l'ordre, en mode CONFIRMATION.
 *
 * Deux appuis séparés par un récapitulatif : le premier ouvre le panneau, le
 * second envoie. Ce n'est pas de la lourdeur administrative — le bouton envoie
 * l'inverse de ce qui est écrit dans le ticket, et voir l'ordre réel avant
 * qu'il parte est tout l'objet de ce mode.
 *
 * L'identifiant d'ordre est engendré **avant** l'envoi et réutilisé en cas de
 * nouvelle tentative : si la réponse se perd, le serveur reconnaît l'ordre
 * déjà passé au lieu d'en ouvrir un second.
 */
export function SendOrderPanel({
  request,
  plan,
  brokerReady,
  currency,
}: {
  request: PlanRequest | null
  plan: PlanResponse | null
  brokerReady: boolean
  currency: string
}) {
  const [confirming, setConfirming] = useState(false)
  // Le chemin d'envoi est partagé avec le bouton posé sous le graphique : une
  // seule implémentation, donc un seul endroit où l'idempotence et le drapeau
  // de confirmation peuvent être oubliés.
  const { busy, result, error, send: sendOrder, reset } = useSendOrder()

  const blocked = !plan?.is_sendable || !request || !brokerReady

  async function send() {
    if (!request) return
    if (await sendOrder(request)) setConfirming(false)
  }

  if (!brokerReady) return null

  return (
    <div className="flex flex-col gap-2">
      {result && <Notice tone="warn" title="Ordre envoyé" items={[result]} />}
      {error && <Notice tone="error" title="Envoi refusé" items={[error]} />}

      {!confirming ? (
        <button
          type="button"
          disabled={blocked}
          onClick={() => {
            reset()
            setConfirming(true)
          }}
          className="min-h-touch w-full rounded-md bg-accent text-[15px] font-semibold text-white disabled:opacity-40"
        >
          {blocked ? 'Ordre non envoyable' : 'Envoyer l’ordre inversé'}
        </button>
      ) : (
        <div className="rounded-lg border border-warn/50 bg-warn/10 p-3">
          <p className="mb-2 text-[12px] font-semibold text-warn">
            Confirmer l’envoi — c’est l’ordre de droite qui part
          </p>

          {plan && (
            <dl className="mb-3 space-y-1 text-[13px]">
              <Line
                label="Sens"
                value={`${plan.sent.side} ${plan.sent.symbol}`}
                tone={plan.sent.side === 'BUY' ? 'up' : 'down'}
              />
              <Line label="Volume" value={`${plan.volume.lots} lot`} />
              <Line label="Stop Loss" value={`${plan.sent.stop_loss_pips ?? '—'} pips`} />
              <Line label="Take Profit" value={`${plan.sent.take_profit_pips ?? '—'} pips`} />
              <Line
                label="Risque"
                value={plan.risk_amount ? `−${plan.risk_amount} ${currency}` : '—'}
                tone="down"
              />
              <Line label="Marge" value={`${plan.margin_required} ${currency}`} />
            </dl>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={send}
              disabled={busy}
              className="min-h-touch flex-1 rounded-md bg-down text-[14px] font-bold text-white disabled:opacity-50"
            >
              {busy ? 'Envoi…' : 'Envoyer'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="min-h-touch flex-1 rounded-md border border-border bg-elevated text-[14px] text-ink-muted"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Line({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: string
  tone?: 'normal' | 'up' | 'down'
}) {
  const tones = { normal: 'text-ink', up: 'text-up', down: 'text-down' } as const
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`num font-semibold ${tones[tone]}`}>{value}</dd>
    </div>
  )
}
